import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { useApi, useApiQuery, type SyncStatusResponse, type TenantConfigResponse } from '@/lib/api';
import { config } from '@/lib/config';

function McpAdmin() {
  const { post } = useApi();
  const { data, loading, reload } = useApiQuery<{ active: boolean }>('/api/admin/mcp');
  const [busy, setBusy] = useState(false);
  const active = data?.active ?? false;

  async function toggle() {
    setBusy(true);
    try {
      await post('/api/admin/mcp/enable', { enabled: !active });
      reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Claude Desktop access (MCP add-on)</p>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            className={`rounded-md px-4 py-1.5 text-sm font-semibold disabled:opacity-60 ${
              active ? 'border border-border text-foreground hover:bg-muted' : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {busy ? '…' : active ? 'Disable' : 'Enable'}
          </button>
        )}
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        {active
          ? 'Enabled — your team can query members from Claude Desktop. Add this MCP endpoint with a Cognito ID token as the bearer:'
          : 'A paid add-on: ask Claude about your members ("who should Megan call today?") from Claude Desktop.'}
      </p>
      {active && <p className="mt-2 break-all rounded-md bg-muted px-3 py-2 font-mono text-xs">{config.mcpUrl}</p>}
    </div>
  );
}

interface TicketedEvent {
  eventId: string;
  name: string;
  date?: string;
  tiers: Array<{ id: string; name: string; priceCents: number }>;
  convenienceFeeCents: number;
}
interface EventsResponse {
  items: TicketedEvent[];
}

function TicketingAdmin({ tenantId }: { tenantId: string }) {
  const { post } = useApi();
  const { data, loading, reload } = useApiQuery<EventsResponse>('/api/events');
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [tierName, setTierName] = useState('General Admission');
  const [price, setPrice] = useState('25');
  const [fee, setFee] = useState('1');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await post('/api/events', {
        name,
        date,
        tiers: [{ name: tierName, priceCents: Math.round(Number(price) * 100) }],
        convenienceFeeCents: Math.round(Number(fee) * 100),
      });
      setName('');
      setMsg('Event created.');
      reload();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  }

  const origin = window.location.origin;
  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-6 shadow-sm">
      <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Event ticketing</p>
      <form onSubmit={onCreate} className="mt-4 grid gap-3 sm:grid-cols-2">
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Event name" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="Date (e.g. Jun 12, 2026)" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <input value={tierName} onChange={(e) => setTierName(e.target.value)} placeholder="Tier name" className="rounded-md border border-border bg-background px-3 py-2 text-sm" />
        <div className="flex gap-2">
          <input value={price} onChange={(e) => setPrice(e.target.value)} type="number" min="0" step="0.01" placeholder="Price $" className="w-1/2 rounded-md border border-border bg-background px-3 py-2 text-sm" />
          <input value={fee} onChange={(e) => setFee(e.target.value)} type="number" min="0" step="0.01" placeholder="Fee $/ticket" className="w-1/2 rounded-md border border-border bg-background px-3 py-2 text-sm" />
        </div>
        <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60 sm:col-span-2">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create ticketed event
        </button>
      </form>
      {msg && <p className="mt-3 text-sm text-muted-foreground">{msg}</p>}

      <div className="mt-6 space-y-2">
        {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        {data?.items?.map((ev) => (
          <div key={ev.eventId} className="rounded-md border border-border p-3 text-sm">
            <p className="font-medium">{ev.name}</p>
            <a href={`${origin}/events/${tenantId}/${ev.eventId}`} className="break-all font-mono text-xs text-primary hover:underline">
              {origin}/events/{tenantId}/{ev.eventId}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The scoring configuration as the scorer reads it, straight from the tenant's
 * config item. Deliberately not a local copy of the numbers: the panel used to
 * hard-code them, and it went on reporting email engagement at zero after
 * Constant Contact was connected and weighted at 0.15.
 *
 * Connected and unconnected signals are listed apart because a zero means two
 * different things. On a connected signal it is a tuning choice; on an
 * unconnected one it is the only safe setting, since weighting a signal with no
 * data behind it pushes every member toward At Risk.
 */
function WeightsPanel() {
  const { data, loading, error } = useApiQuery<TenantConfigResponse>('/api/admin/tenant/config');
  const connected = data?.signals.filter((s) => s.connected) ?? [];
  const pending = data?.signals.filter((s) => !s.connected) ?? [];
  const total = data?.weightTotal ?? 0;
  const totalOff = data !== null && Math.abs(total - 1) > 0.0001;

  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Engagement weights</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Live from tenant config — the values the last scoring run used.
      </p>

      {error ? (
        <p className="mt-4 text-sm text-destructive">{error}</p>
      ) : loading || !data ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : (
        <>
          <div className="mt-5 space-y-4">
            {connected.map((sig) => (
              <div key={sig.key}>
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-sm font-medium">{sig.label}</p>
                  <p className="shrink-0 font-mono text-sm tabular-nums">{sig.weight.toFixed(2)}</p>
                </div>
                <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, sig.weight * 100)}%` }} />
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">{sig.source}</p>
              </div>
            ))}
          </div>

          {pending.length > 0 && (
            <div className="mt-5 border-t border-border pt-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Not connected yet</p>
              <ul className="mt-2 space-y-2">
                {pending.map((sig) => (
                  <li key={sig.key}>
                    <div className="flex items-baseline justify-between gap-4 text-sm">
                      <span className="text-muted-foreground">{sig.label}</span>
                      <span className="shrink-0 font-mono tabular-nums text-muted-foreground/60">{sig.weight.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{sig.source}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <dl className="mt-5 grid grid-cols-2 gap-y-2 border-t border-border pt-4 text-sm">
            <dt className="text-muted-foreground">Total</dt>
            <dd className={`font-mono tabular-nums ${totalOff ? 'text-destructive' : ''}`}>
              {total.toFixed(2)}
              {totalOff && <span className="ml-2 text-xs font-sans">must total 1.00</span>}
            </dd>
            <dt className="text-muted-foreground">Bucket cutoffs</dt>
            <dd className="font-mono tabular-nums">
              {data.thresholds.engaged} / {data.thresholds.watching} / {data.thresholds.atRisk}
            </dd>
            <dt className="text-muted-foreground">Inactivity cap</dt>
            <dd className="font-mono tabular-nums">{data.inactivityFlagMonths} mo</dd>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            Engaged / Watching / At Risk cutoffs on a 0–100 score. {data.inactivityFlagMonths} months with no event
            activity caps a member at At Risk regardless of score.
          </p>
        </>
      )}
    </div>
  );
}

export function TenantAdmin() {
  const { hasRole, user } = useAuth();
  const isAdmin = hasRole('admin');
  const { data, loading, error } = useApiQuery<SyncStatusResponse>(isAdmin ? '/api/admin/sync-status' : null);

  if (!isAdmin) {
    return (
      <div className="container mx-auto max-w-7xl px-6 py-10">
        <h1 className="text-2xl font-bold tracking-tight">Forbidden</h1>
        <p className="mt-2 text-muted-foreground">Admin role required.</p>
      </div>
    );
  }

  const tenantId = data?.tenantId ?? user?.tenantId ?? 'gwinnett';
  return (
    <div className="container mx-auto max-w-7xl px-6 py-10">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">Admin</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">Tenant config</h1>
        <p className="mt-2 text-muted-foreground">Scoring weights, sync status, event ticketing.</p>
      </header>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <WeightsPanel />

        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Sync status</p>
          {error ? (
            <p className="mt-4 text-sm text-destructive">{error}</p>
          ) : loading ? (
            <p className="mt-4 text-sm text-muted-foreground">Checking…</p>
          ) : (
            <>
              <dl className="mt-4 grid grid-cols-2 gap-y-3 text-sm">
                <dt className="text-muted-foreground">Tenant</dt>
                <dd className="font-mono">{tenantId}</dd>
                <dt className="text-muted-foreground">Last run</dt>
                <dd className="font-mono">{data?.lastRun?.status ?? 'none yet'}</dd>
                <dt className="text-muted-foreground">Finished</dt>
                <dd className="font-mono">{data?.lastRun?.endedAt ? new Date(data.lastRun.endedAt).toLocaleString() : '—'}</dd>
              </dl>
              {data?.lastRun?.results && (
                <ul className="mt-4 space-y-1 text-xs text-muted-foreground">
                  {data.lastRun.results.map((r) => (
                    <li key={r.resource}>
                      <span className="font-mono">{r.resource}</span>: {r.written.toLocaleString()} written
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>

      <TicketingAdmin tenantId={tenantId} />
      <McpAdmin />
    </div>
  );
}
